import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Avatar,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Skeleton,
  Card,
  CardContent
} from '@mui/material';
import {
  Search,
  FilterList,
  VideoLibrary,
  Visibility,
  ThumbUp,
  Comment,
  Schedule,
  TrendingUp,
  TrendingDown,
  PlayArrow,
  Info
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { youtubeAPI } from '../../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import SectionTitle from '../crt/SectionTitle';
import Panel from '../crt/Panel';

const VideoAnalytics: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('published_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [minViews, setMinViews] = useState<number>(0);
  const [timeWindowDays, setTimeWindowDays] = useState<number>(0); // 0 = all time
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Fetch videos list
  const { data: videosData, isLoading, refetch } = useQuery({
    queryKey: ['videos', rowsPerPage, page * rowsPerPage, sortBy, sortOrder],
    queryFn: () => youtubeAPI.getVideos(rowsPerPage, page * rowsPerPage, sortBy, sortOrder),
    staleTime: 60000
  });

  // Fetch selected video details
  const { data: videoDetail } = useQuery({
    queryKey: ['video-detail', selectedVideo?.video_id],
    queryFn: () => youtubeAPI.getVideoDetail(selectedVideo.video_id),
    enabled: !!selectedVideo,
    staleTime: 60000
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleVideoClick = (video: any) => {
    setSelectedVideo(video);
    setDetailsOpen(true);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDuration = (duration: string) => {
    // Convert ISO 8601 duration to readable format
    const match = duration?.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    if (!match) return duration;
    
    const hours = (match[1] || '').replace('H', '');
    const minutes = (match[2] || '').replace('M', '');
    const seconds = (match[3] || '').replace('S', '');
    
    const parts = [];
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    if (seconds) parts.push(`${seconds}s`);
    
    return parts.join(' ') || duration;
  };

  const getEngagementRate = (video: any): string => {
    if (!video.view_count) return '0';
    return ((video.like_count + video.comment_count) / video.view_count * 100).toFixed(2);
  };

  return (
    <Box>
      <SectionTitle>VIDEO PERFORMANCE ANALYTICS</SectionTitle>
      {/* Header with Search and Filters */}
      <Panel label="FILTERS" sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="published_at">Published Date</MenuItem>
              <MenuItem value="views">Views</MenuItem>
              <MenuItem value="likes">Likes</MenuItem>
              <MenuItem value="comments">Comments</MenuItem>
            </TextField>
            <TextField
              size="small"
              type="number"
              label="Min Views"
              value={minViews}
              onChange={(e) => setMinViews(parseInt(e.target.value || '0', 10))}
              sx={{ width: 140 }}
            />
            <TextField
              select
              size="small"
              label="Published Within"
              value={timeWindowDays}
              onChange={(e) => setTimeWindowDays(parseInt(e.target.value, 10))}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={0}>All time</MenuItem>
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={90}>90 days</MenuItem>
            </TextField>
          </Box>
        </Box>

        {/* Summary Stats */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Total Videos
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {videosData?.total || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Total Views
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {formatNumber(
                    videosData?.videos.reduce((sum: number, v: any) => sum + (v.view_count || 0), 0) || 0
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Avg Views/Video
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {formatNumber(
                    Math.round(
                      videosData?.videos.reduce((sum: number, v: any) => sum + (v.view_count || 0), 0) / 
                      (videosData?.videos.length || 1) || 0
                    )
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="caption">
                  Avg Engagement
                </Typography>
                <Typography variant="h5" fontWeight={600}>
                  {(
                    videosData?.videos.reduce((sum: number, v: any) => 
                      sum + parseFloat(getEngagementRate(v)), 0
                    ) / (videosData?.videos.length || 1) || 0
                  ).toFixed(2)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Panel>

      {/* Videos Table */}
      <Panel label="VIDEOS" sx={{ p: 0 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Video</TableCell>
                <TableCell>Published</TableCell>
                <TableCell align="right">Views</TableCell>
                <TableCell align="right">Likes</TableCell>
                <TableCell align="right">Comments</TableCell>
                <TableCell align="right">Engagement</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={8}>
                      <Skeleton height={60} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                (videosData?.videos || [])
                  .filter((v: any) => !searchTerm || (v.title?.toLowerCase().includes(searchTerm.toLowerCase())))
                  .filter((v: any) => !minViews || (v.view_count || 0) >= minViews)
                  .filter((v: any) => {
                    if (!timeWindowDays) return true;
                    const pub = v.published_at ? new Date(v.published_at) : null;
                    if (!pub) return true;
                    const since = new Date();
                    since.setDate(since.getDate() - timeWindowDays);
                    return pub >= since;
                  })
                  .filter((video: any) => 
                    !searchTerm || 
                    video.title.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((video: any) => (
                    <TableRow key={video.video_id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            variant="rounded"
                            src={video.thumbnail_url}
                            sx={{ width: 120, height: 68 }}
                          >
                            <VideoLibrary />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" noWrap sx={{ maxWidth: 300 }}>
                              {video.title}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 300 }}>
                              {video.description?.substring(0, 100)}...
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {new Date(video.published_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          <Visibility fontSize="small" color="action" />
                          {formatNumber(video.view_count || 0)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          <ThumbUp fontSize="small" color="action" />
                          {formatNumber(video.like_count || 0)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          <Comment fontSize="small" color="action" />
                          {formatNumber(video.comment_count || 0)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={`${getEngagementRate(video)}%`}
                          color={parseFloat(getEngagementRate(video)) > 5 ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatDuration(video.duration)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleVideoClick(video)}
                        >
                          <Info />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={videosData?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Panel>

      {/* Video Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Video Performance Details
        </DialogTitle>
        <DialogContent>
          {videoDetail && (
            <Box>
              <Box display="flex" gap={2} mb={3}>
                <Avatar
                  variant="rounded"
                  src={videoDetail.video.thumbnail_url}
                  sx={{ width: 160, height: 90 }}
                />
                <Box flex={1}>
                  <Typography variant="h6" gutterBottom>
                    {videoDetail.video.title}
                  </Typography>
                  <Box display="flex" gap={2}>
                    <Chip
                      icon={<Visibility />}
                      label={`${formatNumber(videoDetail.video.view_count)} views`}
                      size="small"
                    />
                    <Chip
                      icon={<ThumbUp />}
                      label={`${formatNumber(videoDetail.video.like_count)} likes`}
                      size="small"
                    />
                    <Chip
                      icon={<Comment />}
                      label={`${formatNumber(videoDetail.video.comment_count)} comments`}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>
                Metrics History (30 days)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={videoDetail.metrics_history}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date"
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tickFormatter={formatNumber} />
                  <Tooltip 
                    formatter={(value: number) => formatNumber(value)}
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                  />
                  <Line type="monotone" dataKey="views" stroke="#1976d2" name="Views" />
                  <Line type="monotone" dataKey="likes" stroke="#4caf50" name="Likes" />
                  <Line type="monotone" dataKey="comments" stroke="#ff9800" name="Comments" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default VideoAnalytics;
